import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type UserMessageUpdateParams } from "@sendbird/chat/message";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface EditTextMessageParams {
  channel: GroupChannel;
  messageId: number;
  message: string;
}

/**
 * Mutation hook to edit a previously sent text message in a Sendbird channel.
 *
 * Only `message` is updated. The original Sendbird `data` (workplace user
 * metadata stamped by `useSendTextMessage`) is left untouched because
 * `updateUserMessage` uses merge semantics for optional fields — omitting
 * `data` preserves the existing value. This matches what Sendbird's own
 * UIKit does on update.
 *
 * @example
 * ```tsx
 * const { mutate: editMessage, isPending } = useEditTextMessage();
 *
 * editMessage({
 *   channel,
 *   messageId: message.messageId,
 *   message: "Hello (edited)!",
 * });
 * ```
 */
export function useEditTextMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ channel, messageId, message }: EditTextMessageParams): Promise<void> => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) {
        throw new Error("Message cannot be empty");
      }

      const params: UserMessageUpdateParams = {
        message: trimmedMessage,
      };

      await channel.updateUserMessage(messageId, params);
    },
    onSuccess: (_, variables) => {
      void queryClient.refetchQueries({
        queryKey: ["chat", "messages", variables.channel.url],
      });
    },
    meta: {
      userErrorMessage: "Failed to edit message",
      logErrorMessage: APP_V2_APP_EVENTS.EDIT_CHAT_MESSAGE_FAILURE,
    },
  });
}
